// src/components/PageProgressBar.jsx
//
// Thin top-of-viewport progress bar, à la NProgress/GitHub, that shows
// whenever axios has in-flight requests. The bar fades in after a short
// delay so quick 50–100ms requests don't cause a flicker; it trickles
// its width while pending, snaps to 100% on completion, then fades out.
//
// Respects `prefers-reduced-motion` — users with that preference see a
// plain in/out fade without the trickling animation.

import React, { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { Box } from '@mui/material';
import requestTracker from '../services/requestTracker';

const FADE_IN_DELAY_MS = 120;
const TRICKLE_INTERVAL_MS = 400;

const useReducedMotion = () => {
  const [reduced, setReduced] = useState(() =>
    typeof window !== 'undefined'
      && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches,
  );
  useEffect(() => {
    const mql = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    if (!mql) return undefined;
    const onChange = () => setReduced(!!mql.matches);
    mql.addEventListener?.('change', onChange);
    return () => mql.removeEventListener?.('change', onChange);
  }, []);
  return reduced;
};

const PageProgressBar = () => {
  const count = useSyncExternalStore(requestTracker.subscribe, requestTracker.getSnapshot, requestTracker.getSnapshot);
  const reducedMotion = useReducedMotion();
  const [visible, setVisible] = useState(false);
  const [progress, setProgress] = useState(0);
  const delayTimerRef = useRef(null);
  const trickleTimerRef = useRef(null);
  const finishTimerRef = useRef(null);

  useEffect(() => {
    if (count > 0) {
      // Debounce the appearance — flash-avoidance.
      if (!visible && !delayTimerRef.current) {
        delayTimerRef.current = setTimeout(() => {
          setVisible(true);
          setProgress(10);
          delayTimerRef.current = null;
        }, FADE_IN_DELAY_MS);
      }
    } else {
      // All requests done — snap to 100% then fade out.
      if (delayTimerRef.current) {
        clearTimeout(delayTimerRef.current);
        delayTimerRef.current = null;
      }
      if (visible) {
        setProgress(100);
        if (finishTimerRef.current) clearTimeout(finishTimerRef.current);
        finishTimerRef.current = setTimeout(() => {
          setVisible(false);
          setProgress(0);
          finishTimerRef.current = null;
        }, 220);
      }
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [count]);

  // Trickle the bar while pending so the user sees continuous motion.
  useEffect(() => {
    if (!visible || reducedMotion) return undefined;
    if (trickleTimerRef.current) clearInterval(trickleTimerRef.current);
    trickleTimerRef.current = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 90) return prev;
        // Logarithmic trickle — fast near start, slow near the cap.
        const delta = (90 - prev) * 0.18;
        return Math.min(90, prev + Math.max(0.5, delta));
      });
    }, TRICKLE_INTERVAL_MS);
    return () => {
      if (trickleTimerRef.current) {
        clearInterval(trickleTimerRef.current);
        trickleTimerRef.current = null;
      }
    };
  }, [visible, reducedMotion]);

  useEffect(() => () => {
    if (delayTimerRef.current) clearTimeout(delayTimerRef.current);
    if (trickleTimerRef.current) clearInterval(trickleTimerRef.current);
    if (finishTimerRef.current) clearTimeout(finishTimerRef.current);
  }, []);

  return (
    <Box
      aria-hidden
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        height: 2,
        zIndex: (t) => t.zIndex.tooltip + 20,
        pointerEvents: 'none',
        opacity: visible ? 1 : 0,
        transition: 'opacity 180ms ease',
      }}
    >
      <Box
        sx={{
          height: '100%',
          width: `${progress}%`,
          background: (t) =>
            `linear-gradient(90deg, ${t.palette.primary.main}, ${t.palette.primary.light})`,
          boxShadow: (t) => `0 0 10px ${t.palette.primary.main}, 0 0 4px ${t.palette.primary.main}`,
          transition: reducedMotion ? 'none' : 'width 240ms ease',
        }}
      />
    </Box>
  );
};

export default PageProgressBar;
